/**
 * HOW THE ENGINE KNOWS THE PERSON ACTUALLY DID IT.
 *
 * An action step declares `until: { <signal>: <target> }`. This file is the table of signals, and it
 * is a plain object keyed by signal name precisely so adding the next one is an entry rather than a
 * change to the overlay — there are other tours coming and they will want more than these five.
 *
 * ── THE SMALLEST SET THAT COVERS THE TWO MACHINE TOURS ───────────────────────────────────────────
 *   clicked   they clicked the highlighted control          "click New"
 *   appears   something new is on screen                    "open the dialog"  → the dialog exists
 *   gone      something is no longer on screen              "close the print sheet"
 *   filled    a field's INPUT stopped being empty           "type the unit number"
 *   answered  a QUESTION has an answer — one, or a set      "answer this section"
 *   route     they are on a different page                  "go to Stickers"
 *   requested a request went out (or was intercepted)       "press Create"     → the POST happened
 *
 * ── `filled` AND `answered` ARE NOT THE SAME QUESTION, AND THE DIFFERENCE COST A JSA ─────────────
 * `filled` reads the DOM: it finds the first form control inside the anchored cell and asks whether
 * it carries text. That is exactly right for a badge-number box, where the typing IS the answer, and
 * it is wrong for every picker — SearchableSelect and ProjectPicker both show their in-flight SEARCH
 * TEXT in the same box that later shows the chosen label. The JSA walkthrough gated two required
 * pickers on `filled`, so typing "Jo" satisfied the gate, the card advanced while the reader was
 * still reading the list, nobody was ever tapped, and the field ended up EMPTY — with the tour having
 * told him it was done. The owner found it by following it: "after typing names when it asks for
 * names, it immediately jumps to the next step. same for the drop down boxes."
 *
 * `answered` asks the COMPONENT instead. Whatever holds the answer publishes `data-tour-answered`
 * (see tourAnchors.js), so a query is not an answer, an empty table row is not an answer, and a value
 * the form would reject is not an answer either. Use `filled` only where the input's text really is
 * the answer; use `answered` everywhere a widget stands between the two.
 *
 * ── EVERY DETECTOR EVALUATES IMMEDIATELY, AND THAT IS A FEATURE ──────────────────────────────────
 * Exploration is how people actually learn an app. Someone who opened the dialog before the tour got
 * round to telling them to must not then be told to open the dialog they are already looking at, so
 * a state signal that is ALREADY true when the step opens satisfies it at once and the tour moves on.
 * `clicked` and `requested` are events rather than states and cannot do this — an event that happened
 * before the step opened is not evidence the person understood the step — so they simply wait.
 *
 * ── WHY MutationObserver AND NOT POLLING ─────────────────────────────────────────────────────────
 * Same reason as useAnchorElement: every module in this app is React.lazy, so the element a step is
 * waiting for routinely arrives several hundred milliseconds after the click that caused it. A
 * one-shot check would call a working app broken. Mutation callbacks are coalesced into one
 * re-evaluation per frame because a React render produces a burst of them.
 */
import { findAnchor, TOUR_ANSWERED_ATTR, TOUR_LABEL_ATTR } from './tourAnchors'
import { subscribePracticeRequests } from './practiceMode'

/** Signals whose value is a data-tour anchor name, so the schema can check the shape. */
export const ANCHOR_SIGNALS = ['clicked', 'appears', 'gone', 'filled', 'answered']

/**
 * Signals whose target may be a LIST of anchors rather than one.
 *
 * Only `answered`, and only because of what a section of a paper form is. The JSA's emergency block
 * asks seven required questions and they are one instruction — "name three people, and say where the
 * hospital is". Seven cards for it would be unbearable and one card gating on one of the seven is how
 * the walkthrough came to march somebody past the other six. So a card may name the whole set, and
 * the reader is held until the set is answered.
 *
 * Deliberately not extended to the others: "advance when any of these three dialogs appears" reads as
 * a mistake far more often than as intent, and there is no step that wants it.
 */
export const MULTI_ANCHOR_SIGNALS = ['answered']

/** Every anchor an `until` names — one for most signals, possibly several for `answered`. */
export const gateAnchorList = (until) => {
  const signal = GATE_SIGNALS.find((k) => until && until[k] != null)
  if (!signal || !ANCHOR_SIGNALS.includes(signal)) return []
  return [].concat(until[signal]).filter((x) => typeof x === 'string' && x.length > 0)
}

const schedule = (fn) => (
  typeof requestAnimationFrame === 'function' ? requestAnimationFrame(fn) : setTimeout(fn, 0)
)
const unschedule = (h) => {
  if (h == null) return
  if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(h)
  else clearTimeout(h)
}

/**
 * ══ A SENTENCE THAT IS STILL BEING TYPED IS NOT A FINISHED ANSWER ════════════════════════════════
 *
 * The second half of the owner's report, and the half that survived the first fix. `answered` reads
 * the payload, so a picker is no longer fooled by its own search text — but a plain text box's answer
 * IS its text, so the box holds an answer after ONE character. "B" is not empty, the card advanced,
 * the overlay pulled focus onto itself (TourOverlay's step-open effect), and a browser routes every
 * subsequent keystroke to document.activeElement — so "reak the pavement out over the main" went
 * nowhere and the sheet kept "B". Reproduced on `scope_of_work`, on the hazard table's first cell,
 * and on `stop_work_detail`, which is the narrative of a stop-work event. Then Submit ACCEPTED it:
 * the walkthrough certified a sheet holding one letter per box as finished, which is the first
 * report ("half the form is empty") wearing the disguise of a full one.
 *
 * So a gate that has become satisfied WAITS while the person is still typing into the box that
 * satisfied it, and the wait ends the moment they are finished with it rather than on a fixed delay:
 *
 *   they keep typing        → the window is pushed out again, so a slow typist is never cut off
 *   they click anything     → a click is not typing. A tap on a picker's option, a checkbox, the next
 *                             field: finished, advance now (this is what keeps the pickers instant)
 *   they press Enter or Tab → same: a key that commits or leaves the box, not one that writes in it
 *   the box loses focus     → they are somewhere else, advance now
 *   they stop for a moment  → TYPING_SETTLES_MS, and then the card may move
 *
 * ONE SECOND AND A FIFTH, which is the app's own autosave debounce (FormFill's SAVE_DEBOUNCE_MS).
 * The number is a judgement about a gap between keystrokes rather than about reading, and matching
 * the autosave means the card moves at about the moment the corner says the answer landed.
 *
 * IT IS NOT A FIX FOR LOST TEXT ON ITS OWN and must not be read as one — a person who pauses mid
 * sentence to think for two seconds still gets an advancing card. What makes that harmless is the
 * other half, in TourOverlay: the card no longer takes focus off a control somebody is typing into.
 * This half is about the card not jumping while they write; that half is about nothing being lost
 * when it does.
 */
export const TYPING_SETTLES_MS = 1200

/** Input types whose value is TYPED one character at a time — not picked, ticked or dialled. */
const TYPED_INPUT_TYPE = /^(text|search|email|tel|url|password|number)$/

/**
 * Is this the kind of control a person writes a sentence into?
 *
 * Deliberately excludes date, time, colour, range, checkbox and radio: their values arrive whole, so
 * there is no half-typed state to protect, and treating them as typing would delay every card that
 * gates on one. Exported because TourOverlay asks the same question about document.activeElement
 * before it moves focus — the two halves of the fix have to agree on what "still writing" means.
 */
export function isTypedInto(el) {
  if (!el || !el.tagName) return false
  if (el.isContentEditable) return true
  if (el.tagName === 'TEXTAREA') return true
  if (el.tagName !== 'INPUT') return false
  return TYPED_INPUT_TYPE.test(String(el.type || 'text').toLowerCase())
}

/**
 * Watch the DOM and re-run `test()` whenever it changes, plus once straight away.
 *
 * @param {object} [options]
 * @param {string[]} [options.extraEvents] DOM events that also re-run the test
 * @param {boolean} [options.settleTyping] hold a satisfied gate while a text box is mid-sentence —
 *        see TYPING_SETTLES_MS. Only the two signals whose answer can BE typed ask for it.
 * @returns {() => void} cleanup
 */
function watchDom(test, satisfy, { extraEvents = [], settleTyping = false } = {}) {
  if (typeof document === 'undefined') return () => {}
  let done = false
  let pending = null
  /** The box being typed into right now, and when the last character landed in it. */
  let typingIn = null
  let typedAt = 0
  let settleTimer = null

  /** How much longer this answer is still being written, in ms. 0 = they are finished with it. */
  const stillWriting = () => {
    if (!settleTyping || !typingIn) return 0
    if (typingIn !== document.activeElement) return 0
    return Math.max(0, typedAt + TYPING_SETTLES_MS - Date.now())
  }

  const check = () => {
    if (done) return
    let ok = false
    try { ok = !!test() } catch { ok = false }
    if (!ok) return
    const writing = stillWriting()
    if (writing > 0) {
      // Re-armed rather than accumulated: every further keystroke pushes `typedAt` out and this
      // timer is replaced, so the card cannot move while somebody is mid-word however slowly they
      // type. Any of the "finished" signals below makes the next check satisfy immediately.
      clearTimeout(settleTimer)
      settleTimer = setTimeout(() => { settleTimer = null; check() }, writing)
      return
    }
    done = true
    clearTimeout(settleTimer)
    settleTimer = null
    satisfy()
  }

  const recheck = () => {
    if (done || pending != null) return
    pending = schedule(() => { pending = null; check() })
  }

  const onEvent = (e) => {
    if (settleTyping) {
      // `input` on a text box is the only event that means "a character just went in". A press
      // (mousedown/pointerdown), a blur, Enter/Tab/Escape and a native `change` all mean the person
      // is FINISHED with the box — which is what keeps a tap on a picker's option instant.
      //
      // ONLY REAL EVENTS DECIDE THIS, never a mutation: a React re-render is what publishes the
      // answer, so every keystroke produces a burst of mutations, and letting those clear the flag
      // would mean the flag was clear at the exact instant it is read. That is this fix failing to
      // fix anything, silently.
      if (e.type === 'input') {
        if (isTypedInto(e.target)) { typingIn = e.target; typedAt = Date.now() }
      } else if (e.type !== 'keydown' || FINISHING_KEY.test(e.key)) {
        typingIn = null
      }
    }
    recheck()
  }

  const observer = new MutationObserver(recheck)
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    characterData: true,
  })
  // A React-controlled <input> updates its value through the DOM property, which mutates NOTHING an
  // observer can see — the `value` attribute is the initial value and never moves. So `filled` also
  // listens for the events that carry a typed character. capture:true because half this app's
  // fields live inside components that stop propagation.
  for (const type of extraEvents) document.addEventListener(type, onEvent, true)

  check()

  return () => {
    done = true
    observer.disconnect()
    for (const type of extraEvents) document.removeEventListener(type, onEvent, true)
    unschedule(pending)
    clearTimeout(settleTimer)
  }
}

/**
 * What a gate over a TYPED answer has to hear.
 *
 * `input`/`change` are how a React-controlled value announces itself at all. The other three are how
 * a person says they are FINISHED with a box — see TYPING_SETTLES_MS. `blur` does not bubble, which
 * is why every one of these is bound with capture:true.
 */
const TYPED_ANSWER_EVENTS = ['input', 'change', 'blur', 'pointerdown', 'mousedown', 'keydown']

/**
 * Keys that mean "done with this box" rather than "another character".
 *
 * The list is short ON PURPOSE. Treating any keydown as an ending is what a first cut of this did,
 * and it undid the whole fix on the first capital letter: Shift is a keydown that writes nothing, so
 * "Break" ended the settle window before the "B" had even arrived.
 */
const FINISHING_KEY = /^(Enter|Tab|Escape)$/

const anchorExists = (name) => findAnchor(name).count > 0

/**
 * A field counts as filled when it has a value the user can see.
 *
 * The anchor may be ON the input, or on a wrapper around one (SearchableSelect renders its own
 * markup and a step points at the component, not at the text box inside it), so both are tried.
 */
function isFilled(name) {
  const { element } = findAnchor(name)
  if (!element) return false
  const field = /^(INPUT|SELECT|TEXTAREA)$/.test(element.tagName)
    ? element
    : element.querySelector('input, select, textarea')
  if (!field) return false
  if (field.type === 'checkbox' || field.type === 'radio') return !!field.checked
  return String(field.value == null ? '' : field.value).trim().length > 0
}

/**
 * What ONE anchored control says about its own answer.
 *
 *   'yes'     answered — the component holding the answer says so.
 *   'no'      on the page, and not answered.
 *   'unasked' not on the page at all. A conditional follow-up whose trigger has not been answered
 *             that way is not an unanswered question — it is a question nobody is being asked, and a
 *             card must not hold somebody for one. (The JSA opens four of these: the first-aid
 *             exception, the re-brief pair, the stop-work detail.)
 *   'mute'    on the page and NOT publishing `data-tour-answered`. Never satisfied, on purpose: a
 *             gate pointed at a control that has not been taught to answer is an authoring mistake,
 *             and a mistake that reads as "already done" is the exact failure this signal exists to
 *             remove. It shows up as a step that will not advance, which is loud.
 */
export function answerState(name) {
  const { element } = findAnchor(name)
  if (!element) return 'unasked'
  const said = element.getAttribute(TOUR_ANSWERED_ATTR)
  if (said == null) return 'mute'
  return said === 'yes' ? 'yes' : 'no'
}

/** What this control is called, for a card that has to say what is still missing. */
const answerLabel = (name) => {
  const { element } = findAnchor(name)
  return (element && element.getAttribute(TOUR_LABEL_ATTR)) || name
}

/**
 * The questions in this gate that are being ASKED and have no answer yet — `[{anchor, label}]`.
 *
 * This is what a card shows while it is holding somebody. "Fill in this section" is useless on a
 * section with seven boxes and one blank, and a walkthrough that stops without saying why is the
 * dead end the stuck timer exists to apologise for.
 */
export function unansweredIn(target) {
  return [].concat(target)
    .map((name) => ({ anchor: name, state: answerState(name) }))
    .filter(({ state }) => state === 'no' || state === 'mute')
    .map(({ anchor }) => ({ anchor, label: answerLabel(anchor) }))
}

/**
 * Is every question this gate names answered?
 *
 * AT LEAST ONE OF THEM HAS TO BE ON THE PAGE. Without that, a gate whose whole set is absent — the
 * sheet still opening, a failed load, the reader on the wrong screen — would read as "all answered"
 * and the card would advance past an instruction nobody could have followed. Every state detector
 * evaluates the instant its step opens (see the header), so that is not a rare race: it is what a
 * lazy-loaded route does on every single step.
 */
export function allAnswered(target) {
  const names = [].concat(target)
  const states = names.map(answerState)
  if (!states.some((s) => s !== 'unasked')) return false
  return states.every((s) => s === 'yes' || s === 'unasked')
}

/**
 * Watch an `answered` gate's set and report the outstanding questions as they change.
 *
 * Shares watchDom's coalescing, so a React render that flips six cells costs one recount. The
 * callback fires once immediately, so a card can say what is missing before anybody touches anything.
 */
export function watchUnanswered(target, report) {
  let last = null
  return watchDom(
    () => {
      const outstanding = unansweredIn(target)
      const key = outstanding.map((o) => o.anchor).join(' ')
      if (key !== last) { last = key; report(outstanding) }
      return false   // never satisfies: this watch is for the card's copy, not for the gate
    },
    () => {},
    { extraEvents: ['input', 'change'] },
  )
}

/**
 * Each detector: (target, env, satisfy) => cleanup. Call `satisfy()` at most once.
 *
 * `env` carries what the DOM cannot answer — currently just the router's pathname, which the
 * provider re-supplies on every navigation.
 */
export const GATE_DETECTORS = {
  /**
   * The click lands on the REAL element, which is why the overlay dims in four panels around it
   * rather than covering it. capture:true so the tour still hears the click when the app's own
   * handler stops propagation, or when the click navigates away and unmounts the button mid-flight.
   */
  clicked: (anchor, env, satisfy) => {
    if (typeof document === 'undefined') return () => {}
    let bound = null
    let done = false
    let pending = null

    const onClick = () => { if (!done) { done = true; satisfy() } }

    const bind = () => {
      if (done) return
      const { element } = findAnchor(anchor)
      if (element === bound) return
      if (bound) bound.removeEventListener('click', onClick, true)
      bound = element
      if (bound) bound.addEventListener('click', onClick, true)
    }

    // Re-bind as the DOM changes: the anchored element is frequently replaced by a re-render between
    // the step opening and the person getting round to pressing it, and a listener on the old node
    // hears nothing.
    const observer = new MutationObserver(() => {
      if (done || pending != null) return
      pending = schedule(() => { pending = null; bind() })
    })
    observer.observe(document.documentElement, { childList: true, subtree: true })
    bind()

    return () => {
      done = true
      observer.disconnect()
      if (bound) bound.removeEventListener('click', onClick, true)
      unschedule(pending)
    }
  },

  appears: (anchor, env, satisfy) => watchDom(() => anchorExists(anchor), satisfy),

  gone: (anchor, env, satisfy) => watchDom(() => !anchorExists(anchor), satisfy),

  filled: (anchor, env, satisfy) => watchDom(
    () => isFilled(anchor),
    satisfy,
    { extraEvents: TYPED_ANSWER_EVENTS, settleTyping: true },
  ),

  /**
   * One anchor, or a whole section's worth of them. `data-tour-answered` flipping is an attribute
   * mutation, which the observer already watches; the input/change listeners ride along because a
   * React-controlled value updates through the DOM property and the render that follows is what
   * flips the attribute — listening for the keystroke as well removes a frame of lag rather than
   * being load-bearing.
   *
   * The rest of TYPED_ANSWER_EVENTS is load-bearing though: they are what tells this watch that a
   * typed answer is FINISHED, and without them a card would sit for the settle delay after a tap
   * that had nothing to do with typing.
   */
  answered: (target, env, satisfy) => watchDom(
    () => allAnswered(target),
    satisfy,
    { extraEvents: TYPED_ANSWER_EVENTS, settleTyping: true },
  ),

  /**
   * Prefix match, not equality: '/timekeeping/report' is still the report screen after the router
   * appends an id, and a step that steered someone there should not un-satisfy itself the moment the
   * app redirects '/fleet' to '/fleet/dashboard'.
   */
  route: (target, env, satisfy) => {
    const here = String((env && env.pathname) || '')
    if (here === target || here.startsWith(`${target}/`)) satisfy()
    return () => {}
  },

  /**
   * `METHOD /path-fragment`. The fragment is matched as a substring of the request path, so a step
   * does not have to know the module's base URL — 'POST /forms-settings/stickers' matches
   * '/api/forms/forms-settings/stickers'.
   *
   * This fires for a request that was INTERCEPTED by practice mode exactly as it does for one that
   * went out, which is the point: on a practice run nothing reaches the server, and the step still
   * has to notice that the person pressed Create.
   */
  requested: (spec, env, satisfy) => {
    const [rawMethod, ...rest] = String(spec).trim().split(/\s+/)
    const method = rawMethod.toUpperCase()
    const fragment = rest.join(' ')
    let done = false
    return subscribePracticeRequests((req) => {
      if (done) return
      if (String(req.method).toUpperCase() !== method) return
      if (fragment && !String(req.path || req.url || '').includes(fragment)) return
      done = true
      satisfy()
    })
  },
}

export const GATE_SIGNALS = Object.keys(GATE_DETECTORS)

/** Signals that are EVENTS. They never self-satisfy on open — see the header. */
export const EVENT_SIGNALS = new Set(['clicked', 'requested'])

/**
 * Start watching one `until`. Returns a cleanup; `satisfy` is called at most once.
 */
export function watchGate(until, env, satisfy) {
  if (!until) return () => {}
  const signal = GATE_SIGNALS.find((k) => until[k] != null)
  if (!signal) return () => {}
  let fired = false
  const once = () => { if (!fired) { fired = true; satisfy() } }
  return GATE_DETECTORS[signal](until[signal], env, once)
}

/** Which signal an `until` uses, or null. */
export function gateSignal(until) {
  if (!until) return null
  return GATE_SIGNALS.find((k) => until[k] != null) || null
}
